import {createSelector} from 'reselect';
import {getPath} from '@epic-core/common';
import {matchPath} from 'react-router';
import {selectLocale} from '../selectors/localization.selectors';
import {FunnelIds} from '../../constants/Tracking';

import {getCollectionMatchParams} from '../../utils/RouteUtils';

export const selectRouterLocation = state => state.getIn(['router', 'location']);
export const selectRouterPathname = state => state.getIn(['router', 'location', 'pathname']);
export const selectRouterSearch = state => state.getIn(['router', 'location', 'search']);
export const selectRouterAction = state => state.getIn(['router', 'action']);

export const selectRouterIsHomeRoute = createSelector(
    selectRouterPathname,
    pathname =>
        pathname &&
        !!matchPath(pathname, {
            path: `${getPath()}:locale/home`,
            exact: false,
            strict: false
        })
);

// NOTE: this method works with pathname passed in to check next pathname
export const selectPathname = createSelector(
    (_, props) => props && props.pathname,
    selectRouterPathname,
    (propsPathname, statePathname) => propsPathname || statePathname
);

export const selectRouterIsCollectionRoute = createSelector(
    selectPathname,
    pathname => /collection/.test(pathname)
);

export const selectRouterIsRedeemRoute = createSelector(
    selectPathname,
    pathname => /redeem/.test(pathname)
);

export const selectCollectionTitle = createSelector(
    selectRouterPathname,
    state => state.getIn(['store', 'collections']),
    (pathname, collections) => {
        const {productName} = getCollectionMatchParams(pathname);
        if (!collections || !productName) return [];

        return collections.getIn([productName, 'title']);
    }
);

// assumes all product detail and news pages will have /product/ in url
export const selectRouterIsProductRoute = createSelector(
    selectPathname,
    pathname => /product/.test(pathname)
);

export const selectRouterIsLibraryRoute = createSelector(
    selectRouterPathname,
    pathname => /library$/.test(pathname)
);

export const selectRouterIsLibraryManageDLCRoute = createSelector(
    selectPathname,
    pathname => /library\/[\w-]*\/content$/.test(pathname)
);

export const selectRouterIsRequestingUERoute = createSelector(
    selectPathname,
    pathname => /UE$/.test(pathname)
);

export const selectRouterProductNameParam = createSelector(
    (_, props) => props && props.subRoute,
    selectRouterPathname,
    (subRoute, pathname) => {
        const productMatch = matchPath(pathname, {
            path: `${getPath()}:locale/${subRoute}/:productName`
        });
        return productMatch && productMatch.params && productMatch.params.productName;
    }
);

export const selectRouterCatalogIdParam = createSelector(
    (_, props) => props && props.subRoute,
    selectRouterPathname,
    (subRoute, pathname) => {
        const productMatch = matchPath(pathname, {
            path: `${getPath()}:locale/${subRoute}/:catalogId`
        });
        return productMatch && productMatch.params && productMatch.params.catalogId;
    }
);

const getPageTitleByRoute = (isPDP, isHome, isLibrary, isCollection, collectionTitle) => {
    if (isPDP) return null;

    const getMessageCodeByPage = page => `diesel.common.page.${page}.title`;

    if (isHome) return getMessageCodeByPage('home');
    if (isLibrary) return getMessageCodeByPage('library');
    if (isCollection) return collectionTitle;

    // Defaults to store
    return getMessageCodeByPage('store');
};

export const selectPageTitleByRoute = createSelector(
    selectRouterIsProductRoute,
    selectRouterIsHomeRoute,
    selectRouterIsLibraryRoute,
    selectRouterIsCollectionRoute,
    selectCollectionTitle,
    getPageTitleByRoute
);

export const selectURI = createSelector(
    selectRouterPathname,
    selectLocale,
    (pathName, locale) => {
        if (!pathName && !locale) {
            return '';
        }
        const parts = pathName.split(locale);
        return parts[1] || '';
    }
);

export const selectProductFunnelIdByRoute = createSelector(
    selectRouterIsHomeRoute,
    selectRouterIsProductRoute,
    (isHome, isProduct) => {
        if (isHome) return FunnelIds.HOME;
        if (isProduct) return FunnelIds.PRODUCT;

        // If no matches, assume it is the library
        return FunnelIds.LIBRARY;
    }
);

export const privateUtils = {
    getPageTitleByRoute
};
